import { type CSSProperties, type ReactNode } from 'react'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHero,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { RequestedDocumentFootnote } from '@/features/jobs/details/tax-type/info-gathering/documents/components/RequestedDocumentCard'
import styles from './RequestedDocumentDialog.module.css'
import cx from 'classnames'
import { m, useTransform } from 'motion/react'
import { useElementSize } from '@/hooks/element-hooks'
import { useImageLoadingState } from '@/contexts/image-context'
import { SkeletonOverlay } from '@/components/Skeleton'

export interface RequestedDocumentDialogProps {
  /**
   * The content to render in the dialog.
   */
  children?: ReactNode
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Callback when the dialog should close.
   */
  onClose?: () => unknown
  /**
   * The title of the document.
   */
  title: string
  /**
   * The description of the document.
   */
  description?: string
  /**
   * The URL of the image to display.
   */
  imageUrl?: string | null
  /**
   * The variant of the image display.
   */
  imageVariant?: 'document' | 'decoration'
  /**
   * Test ID attribute.
   */
  testId?: string
  /**
   * Test ID attribute for the close button.
   */
  closeTestId?: string
}

/**
 * Dialog for a requested document item.
 */
export function RequestedDocumentDialog({
  isOpen,
  onClose,
  children,
  title,
  description,
  imageUrl,
  imageVariant = 'decoration',
  testId,
  closeTestId,
}: RequestedDocumentDialogProps) {
  const {
    sizeMotionValues: { width },
    connectRef,
  } = useElementSize()
  const widthPx = useTransform(() => width.get() + 'px')

  const { onLoad, loaded } = useImageLoadingState({ src: imageUrl })

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      sheetSizing="auto"
      testId={testId}
      hero={
        !!imageUrl && (
          <ModalDialogHero variant="background" spacing="compact">
            <div
              ref={connectRef}
              aria-hidden
              className={cx(
                styles.imageContainer,
                imageVariant === 'document' && styles.document,
              )}
            >
              {!loaded && imageVariant === 'document' && (
                <SkeletonOverlay color="secondary" />
              )}
              <m.img
                className={styles.image}
                onLoad={onLoad}
                src={imageUrl}
                alt=""
                aria-hidden
                style={
                  {
                    // Needed to calculate the estimated height to prevent
                    // layout shifts.
                    '--image-intrinsic-width': widthPx,
                  } as CSSProperties
                }
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: loaded ? 1 : 0,
                }}
              />
            </div>
          </ModalDialogHero>
        )
      }
    >
      <ModalDialogContent>
        {/*
          Putting title and description in content so they scroll.
          Necessary because the description can get rather lengthy.
        */}
        <div className={styles.content}>
          <ModalDialogTitle>{title}</ModalDialogTitle>
          {description && (
            <ModalDialogDescription>{description}</ModalDialogDescription>
          )}
        </div>
        {children}
        <RequestedDocumentFootnote />
      </ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Close"
            variant="outline"
            onPress={onClose}
            testId={closeTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
